import { useState, useRef, useCallback, useEffect } from 'react';
import type { RefCallback } from 'react';
import {
  SheetElements,
  getClientY,
  getSheetElements,
  freezeTransitions,
  applyDrag,
  releaseTransitions,
  resetDrag,
  shouldClose,
  isInteractiveTarget,
} from './bottomSheetDragHelpers';

interface UseBottomSheetDragProps {
  onClose: () => void;
  enabled?: boolean;
  closeThreshold?: number;
}

const EMULATED_MOUSE_GUARD_MS = 500;

export const useBottomSheetDrag = ({
  onClose,
  enabled = true,
  closeThreshold = 0.3,
}: UseBottomSheetDragProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null);
  const startYRef = useRef(0);
  const startTimeRef = useRef(0);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const setContentRef = useCallback<RefCallback<HTMLDivElement>>((node) => {
    contentRef.current = node;
    setContentEl(node);
  }, []);
  const isDraggingRef = useRef(false);
  const dragYRef = useRef(0);
  const elementsRef = useRef<SheetElements | null>(null);
  const lastTouchTimeRef = useRef(0);

  const enabledRef = useRef(enabled);
  const closeThresholdRef = useRef(closeThreshold);
  const onCloseRef = useRef(onClose);
  enabledRef.current = enabled;
  closeThresholdRef.current = closeThreshold;
  onCloseRef.current = onClose;

  const beginDrag = useCallback((event: TouchEvent | MouseEvent): boolean => {
    if (!enabledRef.current || !contentRef.current) return false;

    const els = getSheetElements(contentRef.current);
    elementsRef.current = els;
    startYRef.current = getClientY(event);
    startTimeRef.current = Date.now();
    isDraggingRef.current = true;
    dragYRef.current = 0;
    setIsDragging(true);
    setDragY(0);
    freezeTransitions(els);

    return true;
  }, []);

  const handleMove = useCallback((event: TouchEvent | MouseEvent) => {
    if (!isDraggingRef.current || !elementsRef.current) return;

    const deltaY = getClientY(event) - startYRef.current;
    if (deltaY > 0) {
      event.preventDefault();
      dragYRef.current = deltaY;
      setDragY(deltaY);
      applyDrag(elementsRef.current, deltaY);
    }
  }, []);

  const handleEnd = useCallback(() => {
    if (!isDraggingRef.current || !elementsRef.current) return;

    const els = elementsRef.current;
    const close = shouldClose({
      dragY: dragYRef.current,
      durationMs: Date.now() - startTimeRef.current,
      contentHeight: els.contentHeight,
      closeThreshold: closeThresholdRef.current,
    });

    if (close) {
      els.content.style.transition = 'transform 150ms ease-in';
      els.content.style.transform = 'translateY(100%)';
      onCloseRef.current();
    } else {
      releaseTransitions(els);
      dragYRef.current = 0;
      setDragY(0);
      resetDrag(els);
    }

    isDraggingRef.current = false;
    elementsRef.current = null;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!enabled || !contentEl) return;

    const grabber = contentEl.querySelector('.bottom-sheet-modal__grabber');
    const header = contentEl.querySelector('.bottom-sheet-modal__header');
    const elements = [grabber, header].filter(Boolean) as Element[];

    let touchMoveHandler: ((e: TouchEvent) => void) | null = null;
    let touchEndHandler: (() => void) | null = null;

    const detachTouch = () => {
      if (touchMoveHandler) document.removeEventListener('touchmove', touchMoveHandler);
      if (touchEndHandler) document.removeEventListener('touchend', touchEndHandler);
      touchMoveHandler = null;
      touchEndHandler = null;
    };

    const handleTouchStart = (e: TouchEvent) => {
      lastTouchTimeRef.current = Date.now();
      if (!beginDrag(e)) return;
      touchMoveHandler = (ev: TouchEvent) => handleMove(ev);
      touchEndHandler = () => {
        lastTouchTimeRef.current = Date.now();
        handleEnd();
        detachTouch();
      };
      document.addEventListener('touchmove', touchMoveHandler, { passive: false });
      document.addEventListener('touchend', touchEndHandler);
    };

    let mouseMoveHandler: ((e: MouseEvent) => void) | null = null;
    let mouseUpHandler: (() => void) | null = null;

    const detachMouse = () => {
      document.body.style.userSelect = '';
      if (mouseMoveHandler) document.removeEventListener('mousemove', mouseMoveHandler);
      if (mouseUpHandler) document.removeEventListener('mouseup', mouseUpHandler);
      mouseMoveHandler = null;
      mouseUpHandler = null;
    };

    const handleMouseDown = (e: MouseEvent) => {
      if (Date.now() - lastTouchTimeRef.current < EMULATED_MOUSE_GUARD_MS) return;
      if (e.button !== 0 || isInteractiveTarget(e.target)) return;
      if (!beginDrag(e)) return;
      document.body.style.userSelect = 'none';
      mouseMoveHandler = (ev: MouseEvent) => handleMove(ev);
      mouseUpHandler = () => {
        handleEnd();
        detachMouse();
      };
      document.addEventListener('mousemove', mouseMoveHandler);
      document.addEventListener('mouseup', mouseUpHandler);
    };

    elements.forEach((element) => {
      element.addEventListener('touchstart', handleTouchStart, { passive: false });
      element.addEventListener('mousedown', handleMouseDown as EventListener);
    });

    return () => {
      elements.forEach((element) => {
        element.removeEventListener('touchstart', handleTouchStart);
        element.removeEventListener('mousedown', handleMouseDown as EventListener);
      });
      detachTouch();
      detachMouse();
    };
  }, [enabled, contentEl, beginDrag, handleMove, handleEnd]);

  return {
    setContentRef,
    isDragging,
    dragY,
  };
};
